import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Table, message, Tag, Statistic, Empty, Tooltip, Progress, Divider, Button } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Pie } from '@ant-design/plots';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  TrophyOutlined,
  FileTextOutlined,
  CalendarOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  EyeOutlined,
  RiseOutlined,
  FallOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';

const CustomerDashboard = () => {
  const { t } = useTranslation();

  const [supplierData, setSupplierData] = useState(null);
  const [certificateData, setCertificateData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const user = JSON.parse(localStorage.getItem('user'));
  const supplierId = user?.supplierId;
  const token = localStorage.getItem('urlToken');

  useEffect(() => {
    const fetchSupplierData = async () => {
      if (!supplierId || !token) {
        message.error(t('supplier_id_missing'));
        return;
      }

      setLoading(true);

      try {
        const supplierResponse = await axios.get(`${apiUrl}/api/Suppliers/${supplierId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSupplierData(supplierResponse.data);

        try {
          const certificatesResponse = await axios.get(
            `${apiUrl}/api/Certificates/supplier/${supplierId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const certs = Array.isArray(certificatesResponse.data)
            ? certificatesResponse.data
            : certificatesResponse.data.certificates || [];
          setCertificateData(certs);
        } catch (certError) {
          console.warn('Certificate fetch error:', certError);
          setCertificateData([]);
        }

        try {
          const auditResponse = await axios.get(
            `${apiUrl}/api/SupplierAudit/supplier/${supplierId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const auditsData = auditResponse.data.audits || [];
          auditsData.sort((a, b) => new Date(a.auditDate) - new Date(b.auditDate));
          setIssues(auditsData);
        } catch (auditError) {
          console.warn('Audit fetch error:', auditError);
          setIssues([]);
        }

        try {
          const performanceResponse = await axios.get(
            `${apiUrl}/api/SupplierPerformance/my-performance`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPerformanceData(performanceResponse.data);
        } catch (perfError) {
          console.warn('Performance fetch error:', perfError);
        }
      } catch (error) {
        console.error('Supplier fetch error:', error);
        message.error(t('data_fetch_error'));
      } finally {
        setLoading(false);
      }
    };

    if (supplierId && token) {
      fetchSupplierData();
    }
  }, [supplierId, token, apiUrl, t]);

  const handleView = async (certificateId) => {
    try {
      const response = await axios.get(`${apiUrl}/api/Certificates/${certificateId}/sas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sasUrl = response.data.sasUrl;

      if (sasUrl) {
        window.open(sasUrl, '_blank');
      } else {
        message.error(t('sas_url_error'));
      }
    } catch (error) {
      message.error(t('certificate_fetch_error'));
      console.error('SAS URL Fetch Error:', error);
    }
  };

  const performanceScore = performanceData?.latestPerformance?.totalScore || supplierData?.supplierPerformance || 0;

  const totalCerts = certificateData.length;
  const approvedCerts = certificateData.filter(cert => cert.situation !== '0').length;
  const pendingCerts = certificateData.filter(cert => cert.situation === '0').length;
  const expiringSoon = certificateData.filter(cert => {
    const daysUntilExpiry = moment(cert.certificateValidityDate).diff(moment(), 'days');
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  }).length;

  const upcomingAudit = issues.find(issue => moment(issue.auditDate).isAfter(moment()));

  const getPerformanceColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    return '#ff4d4f';
  };

  const pieConfig = {
    data: [
      { type: t('achieved'), value: performanceScore },
      { type: t('remaining'), value: 100 - performanceScore },
    ],
    angleField: 'value',
    colorField: 'type',
    radius: 0.9,
    innerRadius: 0.64,
    legend: false,
    statistic: {
      title: {
        offsetY: -8,
        content: t('score'),
        style: { fontSize: '13px', color: '#8c8c8c' },
      },
      content: {
        offsetY: 4,
        style: {
          fontSize: '28px',
          fontWeight: '700',
          color: getPerformanceColor(performanceScore),
        },
        content: `${Math.round(performanceScore)}`,
      },
    },
    color: ({ type }) => {
      return type === t('achieved')
        ? getPerformanceColor(performanceScore)
        : '#f0f0f0';
    },
    label: false,
  };

  const calendarEvents = [
    ...certificateData.map(cert => ({
      title: cert.certificateName,
      date: moment(cert.certificateValidityDate).format('YYYY-MM-DD'),
      backgroundColor: '#ff4d4f',
      borderColor: '#ff4d4f',
      textColor: '#fff',
      extendedProps: {
        type: 'certificate',
        description: `${t('certificate_expiry')}: ${cert.certificateName}`,
      }
    })),
    ...issues.map(issue => ({
      title: t('audits'),
      date: moment(issue.auditDate).format('YYYY-MM-DD'),
      backgroundColor: '#1890ff',
      borderColor: '#1890ff',
      textColor: '#fff',
      extendedProps: {
        type: 'audit',
        description: t('audit_scheduled'),
      }
    }))
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#262626' }}>
          {t('dashboard')}
        </h2>
        <p style={{ margin: '4px 0 0 0', color: '#8c8c8c', fontSize: '14px' }}>
          {t('welcome_back')}, {supplierData?.supplierName || user?.name}
        </p>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '8px' }}>
            <Statistic
              title={t('performance_score')}
              value={Math.round(performanceScore)}
              suffix="/ 100"
              prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
              valueStyle={{
                color: getPerformanceColor(performanceScore),
                fontSize: '28px',
                fontWeight: '600',
              }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '8px' }}>
            <Statistic
              title={t('total_certificates')}
              value={totalCerts}
              prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ fontSize: '28px', fontWeight: '600', color: '#262626' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '8px' }}>
            <Statistic
              title={t('approved')}
              value={approvedCerts}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ fontSize: '28px', fontWeight: '600', color: '#262626' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '8px' }}>
            <Statistic
              title={t('expiring_soon')}
              value={expiringSoon}
              prefix={<ClockCircleOutlined style={{ color: expiringSoon > 0 ? '#faad14' : '#52c41a' }} />}
              valueStyle={{
                fontSize: '28px',
                fontWeight: '600',
                color: expiringSoon > 0 ? '#faad14' : '#262626',
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[16, 16]}>
        {/* Left Column - Certificates Table */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                {t('certificates')}
              </span>
            }
            bordered={false}
            style={{ borderRadius: '8px', height: '100%' }}
            loading={loading}
            bodyStyle={{ padding: '16px' }}
          >
            <Table
              dataSource={certificateData}
              columns={[
                {
                  title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('certificate_name')}</span>,
                  dataIndex: 'certificateName',
                  key: 'certificateName',
                  width: '35%',
                  ellipsis: true,
                  render: (text) => (
                    <span style={{ fontSize: '13px', color: '#262626', fontWeight: '500' }}>{text}</span>
                  ),
                },
                {
                  title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('expiration_date')}</span>,
                  dataIndex: 'certificateValidityDate',
                  key: 'certificateValidityDate',
                  width: '30%',
                  render: (text) => {
                    const daysUntilExpiry = moment(text).diff(moment(), 'days');
                    const isExpiringSoon = daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
                    const isExpired = daysUntilExpiry < 0;

                    return (
                      <div>
                        <div style={{
                          fontSize: '13px',
                          color: isExpired ? '#ff4d4f' : isExpiringSoon ? '#faad14' : '#595959',
                          fontWeight: '500',
                          marginBottom: '2px',
                        }}>
                          {moment(text).format('DD/MM/YYYY')}
                        </div>
                        {(isExpired || isExpiringSoon) && (
                          <div style={{
                            fontSize: '11px',
                            color: isExpired ? '#ff4d4f' : '#faad14',
                          }}>
                            {isExpired ? t('expired') : `${daysUntilExpiry} ${t('days_left')}`}
                          </div>
                        )}
                      </div>
                    );
                  },
                  sorter: (a, b) => moment(a.certificateValidityDate).unix() - moment(b.certificateValidityDate).unix(),
                },
                {
                  title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('status')}</span>,
                  key: 'status',
                  width: '20%',
                  align: 'center',
                  render: (_, record) => {
                    const isPending = record.situation === '0';
                    return (
                      <Tag
                        icon={isPending ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
                        color={isPending ? 'orange' : 'green'}
                        style={{
                          margin: 0,
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                        }}
                      >
                        {isPending ? t('pending') : t('approved')}
                      </Tag>
                    );
                  },
                },
                {
                  title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('action')}</span>,
                  key: 'action',
                  width: '15%',
                  align: 'center',
                  render: (_, record) => (
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => handleView(record.certificateId)}
                      style={{
                        padding: 0,
                        height: 'auto',
                        fontSize: '13px',
                      }}
                    >
                      {t('view')}
                    </Button>
                  ),
                },
              ]}
              rowKey="certificateId"
              pagination={{
                pageSize: 5,
                showSizeChanger: false,
                size: 'small',
                style: { marginBottom: 0, marginTop: '12px' },
              }}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span style={{ color: '#8c8c8c', fontSize: '13px' }}>
                        {t('no_certificates_found')}
                      </span>
                    }
                    style={{ padding: '30px 0' }}
                  />
                ),
              }}
            />
          </Card>
        </Col>

        {/* Right Column - Performance & Calendar */}
        <Col xs={24} lg={12}>
          <Row gutter={[16, 16]}>
            {/* Performance Pie Chart */}
            <Col span={24}>
              <Card
                title={
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                    <TrophyOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                    {t('performance_overview')}
                  </span>
                }
                bordered={false}
                style={{ borderRadius: '8px' }}
                loading={loading}
                bodyStyle={{ padding: '20px' }}
              >
                <Row gutter={16} align="middle">
                  <Col span={12}>
                    <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Pie {...pieConfig} height={200} />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: '0 8px' }}>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
                          {t('performance_score')}
                        </div>
                        <div style={{
                          fontSize: '32px',
                          fontWeight: '700',
                          color: getPerformanceColor(performanceScore),
                        }}>
                          {Math.round(performanceScore)}
                        </div>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', color: '#8c8c8c' }}>{t('progress')}</span>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#595959' }}>
                            {Math.round(performanceScore)}%
                          </span>
                        </div>
                        <Progress
                          percent={performanceScore}
                          strokeColor={getPerformanceColor(performanceScore)}
                          showInfo={false}
                          size="small"
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '12px' }}>
                        {performanceScore >= 75 ? (
                          <RiseOutlined style={{ fontSize: '14px', color: '#52c41a' }} />
                        ) : (
                          <FallOutlined style={{ fontSize: '14px', color: '#ff4d4f' }} />
                        )}
                        <span style={{ fontSize: '12px', color: '#595959' }}>
                          {performanceScore >= 75 ? t('trending_up') : t('trending_down')}
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* Calendar */}
            <Col span={24}>
              <Card
                title={
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                    <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    {t('calendar')}
                  </span>
                }
                bordered={false}
                style={{ borderRadius: '8px' }}
                bodyStyle={{ padding: '16px' }}
              >
                <div style={{ padding: '0' }}>
                  <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    events={calendarEvents}
                    height="auto"
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: ''
                    }}
                    eventClick={(info) => {
                      message.info(info.event.extendedProps.description);
                    }}
                    eventContent={(eventInfo) => {
                      return (
                        <Tooltip title={eventInfo.event.extendedProps.description}>
                          <div style={{
                            fontSize: '11px',
                            padding: '2px 4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {eventInfo.event.title}
                          </div>
                        </Tooltip>
                      );
                    }}
                  />
                </div>
                {/* Calendar Legend */}
                <div style={{
                  marginTop: '12px',
                  padding: '10px',
                  backgroundColor: '#fafafa',
                  borderRadius: '6px',
                  border: '1px solid #f0f0f0',
                }}>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#ff4d4f',
                        borderRadius: '2px',
                      }} />
                      <span style={{ color: '#595959' }}>{t('certificate_expiry')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#1890ff',
                        borderRadius: '2px',
                      }} />
                      <span style={{ color: '#595959' }}>{t('audit_date')}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Bottom Row - Company Info & Next Audit */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        {/* Company Info */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                <SafetyOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                {t('company_info')}
              </span>
            }
            bordered={false}
            style={{ borderRadius: '8px', height: '240px' }}
            loading={loading}
            bodyStyle={{
              height: 'calc(100% - 57px)',
              padding: '24px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {supplierData ? (
              <div style={{ width: '100%' }}>
                <Row gutter={[24, 20]}>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#e6f7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <IdcardOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                          {t('company_name')}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#262626',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {supplierData.supplierName}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#f6ffed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <TagOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                          {t('supplier_code')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                          {supplierData.supplierCode}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#fffbe6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <FileTextOutlined style={{ fontSize: '18px', color: '#faad14' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                          {t('category')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                          {supplierData.category}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#f9f0ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <UserOutlined style={{ fontSize: '18px', color: '#722ed1' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                          {t('contact')}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                          {supplierData.businessContact}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#e6f7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <MailOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                          {t('email')}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#1890ff',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {supplierData.businessEmail}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#fff1f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <EnvironmentOutlined style={{ fontSize: '18px', color: '#ff4d4f' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                          {t('address')}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#262626',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {supplierData.address}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}>
                <p style={{ color: '#8c8c8c', fontSize: '13px', margin: 0 }}>
                  {t('loading_company_info')}
                </p>
              </div>
            )}
          </Card>
        </Col>

        {/* Next Audit */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                {t('next_audit')}
              </span>
            }
            bordered={false}
            style={{ borderRadius: '8px', height: '240px' }}
            loading={loading}
            bodyStyle={{
              height: 'calc(100% - 57px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              overflow: 'hidden',
            }}
          >
            {upcomingAudit ? (
              <div style={{ width: '100%', maxWidth: '100%' }}>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f0f5ff',
                  borderRadius: '8px',
                  border: '1px solid #d6e4ff',
                  textAlign: 'center',
                }}>
                  <div style={{
                    fontSize: '48px',
                    fontWeight: '700',
                    color: '#1890ff',
                    marginBottom: '8px',
                    lineHeight: 1,
                  }}>
                    {moment(upcomingAudit.auditDate).format('DD')}
                  </div>
                  <div style={{
                    fontSize: '15px',
                    color: '#595959',
                    fontWeight: '500',
                    marginBottom: '12px',
                  }}>
                    {moment(upcomingAudit.auditDate).format('MMMM YYYY')}
                  </div>
                  <Divider style={{ margin: '12px 0' }} />
                  <div style={{
                    fontSize: '13px',
                    color: '#8c8c8c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}>
                    <ClockCircleOutlined />
                    <span>{moment(upcomingAudit.auditDate).fromNow()}</span>
                  </div>
                </div>
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: '#8c8c8c', fontSize: '13px' }}>
                    {t('no_upcoming_audits')}
                  </span>
                }
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* FullCalendar CSS Override */}
      <style jsx global>{`
        .fc {
          font-size: 13px;
        }
        .fc .fc-button {
          padding: 4px 12px;
          font-size: 13px;
          border-radius: 4px;
        }
        .fc .fc-toolbar-title {
          font-size: 15px;
          font-weight: 600;
          color: #262626;
        }
        .fc .fc-daygrid-day-number {
          font-size: 12px;
          padding: 4px;
          color: #595959;
        }
        .fc .fc-event {
          border-radius: 3px;
          padding: 2px 4px;
          font-size: 11px;
          cursor: pointer;
        }
        .fc .fc-daygrid-day-frame {
          min-height: 60px;
        }
        .fc-theme-standard td, .fc-theme-standard th {
          border-color: #f0f0f0;
        }
        .fc .fc-button-primary {
          background-color: #1890ff;
          border-color: #1890ff;
        }
        .fc .fc-button-primary:hover {
          background-color: #40a9ff;
          border-color: #40a9ff;
        }
        .fc .fc-button-primary:disabled {
          background-color: #d9d9d9;
          border-color: #d9d9d9;
        }
        .fc .fc-col-header-cell {
          background-color: #fafafa;
          font-weight: 600;
          color: #595959;
        }
      `}</style>
    </div>
  );
};

export default CustomerDashboard;
